import { useEffect, useState } from 'react'
import type { AnsageTeam } from '../lib/api'

/** Die Trommel: erst schnell, dann langsamer, insgesamt etwa so lange. */
export const TROMMEL_MS = 5000

/**
 * Die Schritte der Trommel — abwechselnd a und b, mit wachsenden Abständen,
 * und der letzte Schritt ist immer der Gewinner (bei Unentschieden: a, am
 * Ende leuchten beide). Reine Funktion, damit der Test sie nachrechnen kann.
 */
export function trommelSchritte(
  gewinner: 'a' | 'b' | 'unentschieden',
  gesamtMs = TROMMEL_MS,
): Array<{ seite: 'a' | 'b'; nach: number }> {
  const schritte: Array<{ seite: 'a' | 'b'; nach: number }> = []
  let abstand = 90
  let t = 0
  let seite: 'a' | 'b' = 'a'
  while (t + abstand < gesamtMs || schritte.length < 6) {
    t += abstand
    schritte.push({ seite, nach: t })
    seite = seite === 'a' ? 'b' : 'a'
    abstand = Math.min(Math.round(abstand * 1.13), 700)
  }
  const ziel: 'a' | 'b' = gewinner === 'b' ? 'b' : 'a'
  if (schritte[schritte.length - 1]!.seite !== ziel) {
    schritte.push({ seite: ziel, nach: t + abstand })
  }
  return schritte
}

/**
 * Die Auslosung nach der Ansage (Flo, 31.10.): zwei Mannschaften mit Logo,
 * ein Lauflicht, das langsamer wird und auf dem Ergebnis aus dem Admin
 * stehen bleibt. Ein Spiel für die Bühne — das Ergebnis ist kein Zufall,
 * und das steht auch nirgends anders.
 */
export function Auslosung({
  teamA,
  teamB,
  gewinner,
  ergebnis,
  onFertig,
}: {
  teamA: AnsageTeam
  teamB: AnsageTeam
  gewinner: 'a' | 'b' | 'unentschieden'
  ergebnis: string
  onFertig?: () => void
}) {
  const [aktiv, setAktiv] = useState<'a' | 'b' | null>(null)
  const [fertig, setFertig] = useState(false)

  useEffect(() => {
    const timer: ReturnType<typeof setTimeout>[] = []
    for (const s of trommelSchritte(gewinner)) {
      timer.push(setTimeout(() => setAktiv(s.seite), s.nach))
    }
    const ende = trommelSchritte(gewinner).at(-1)!.nach + 400
    timer.push(
      setTimeout(() => {
        setFertig(true)
        onFertig?.()
      }, ende),
    )
    return () => timer.forEach(clearTimeout)
    // Einmal je Auslosung — die Seite gibt ihr je Durchlauf einen neuen `key`.
  }, [])

  const leuchtet = (seite: 'a' | 'b') =>
    fertig ? gewinner === 'unentschieden' || gewinner === seite : aktiv === seite
  const sieger = gewinner === 'a' ? teamA.name : gewinner === 'b' ? teamB.name : null

  return (
    <section
      className="flex w-full max-w-3xl flex-col items-center gap-6"
      data-testid="auslosung"
      data-fertig={fertig}
      aria-live="polite"
    >
      <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-4">
        <Mannschaft team={teamA} leuchtet={leuchtet('a')} fertig={fertig} seite="a" />
        <span className="text-3xl font-semibold text-text-muted">vs.</span>
        <Mannschaft team={teamB} leuchtet={leuchtet('b')} fertig={fertig} seite="b" />
      </div>
      <p className="min-h-[2.5rem] text-2xl font-semibold" data-testid="auslosung-ergebnis">
        {fertig
          ? sieger
            ? `K4ias Tipp: ${sieger} gewinnt${ergebnis ? ` ${ergebnis}` : ''}.`
            : `K4ias Tipp: Unentschieden${ergebnis ? ` ${ergebnis}` : ''}.`
          : 'K4ia lost aus …'}
      </p>
    </section>
  )
}

function Mannschaft({
  team,
  leuchtet,
  fertig,
  seite,
}: {
  team: AnsageTeam
  leuchtet: boolean
  fertig: boolean
  seite: 'a' | 'b'
}) {
  return (
    <div
      className={`flex flex-col items-center gap-3 rounded-3xl border-4 bg-white p-6 transition-all duration-150 ${
        leuchtet
          ? 'scale-105 border-brand shadow-[0_0_40px_rgba(99,195,209,0.6)]'
          : fertig
            ? 'border-transparent opacity-40'
            : 'border-transparent'
      }`}
      data-testid={`team-${seite}`}
      data-leuchtet={leuchtet}
    >
      {team.logo ? (
        <img
          src={team.logo}
          alt={team.name}
          className="h-[clamp(5rem,22vh,12rem)] w-auto max-w-full object-contain"
          draggable={false}
        />
      ) : (
        <div className="flex h-[clamp(5rem,22vh,12rem)] items-center justify-center text-4xl font-bold text-neutral-800">
          {team.name.slice(0, 2).toUpperCase()}
        </div>
      )}
      <p className="text-xl font-semibold text-neutral-900">{team.name}</p>
    </div>
  )
}
